import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { SuitabilityMeters, PriceTag } from "./PluginCard";
import { ExternalLink, Loader2, Flag } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const FeedbackStrip = ({ detail }) => {
  const { user, openAuth } = useAuth();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("broken_link");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          kind: "plugin_issue", plugin_id: detail.id, plugin_name: detail.name,
          category, text,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed to submit");
      toast.success("Thanks — feedback sent to the team");
      setText("");
      setOpen(false);
    } catch (err) {
      toast.error(String(err.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-5 border-t border-white/10 pt-4">
      {!open ? (
        <button
          data-testid="report-issue-btn"
          onClick={() => (user ? setOpen(true) : openAuth())}
          className="flex items-center gap-1.5 text-xs text-white/35 transition-colors duration-200 hover:text-amber-300"
        >
          <Flag className="h-3 w-3" /> Report an issue with this plugin
        </button>
      ) : (
        <form onSubmit={submit} className="space-y-2.5">
          <div className="flex gap-2">
            {[["broken_link", "Broken link"], ["wrong_info", "Wrong info"], ["other", "Other"]].map(([val, label]) => (
              <button type="button" key={val} data-testid={`issue-cat-${val}`}
                      onClick={() => setCategory(val)}
                      className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors duration-150 ${
                        category === val ? "bg-amber-500 text-black" : "bg-white/5 text-white/50 hover:text-white"
                      }`}>
                {label}
              </button>
            ))}
          </div>
          <textarea
            data-testid="issue-text-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="What's wrong?"
            rows={2}
            required
            className="w-full rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm text-white placeholder-white/25 outline-none focus:border-amber-500/50"
          />
          <div className="flex gap-2">
            <button data-testid="issue-submit-btn" disabled={busy}
                    className="rounded-lg bg-amber-500 px-4 py-1.5 text-xs font-semibold text-black transition-colors duration-200 hover:bg-amber-400 disabled:opacity-50">
              Send report
            </button>
            <button type="button" onClick={() => setOpen(false)} className="text-xs text-white/40 hover:text-white">Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
};

const Cell = ({ label, children, span = 1, testId }) => (
  <div data-testid={testId} className={`border border-white/10 p-4 ${span === 4 ? "md:col-span-4" : span === 2 ? "md:col-span-2" : "md:col-span-1"}`}>
    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">{label}</div>
    <div className="mt-2 text-sm leading-relaxed text-white/80">{children}</div>
  </div>
);

const TagList = ({ items, accent }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.map((t) => (
      <span key={t} className={`rounded px-2 py-0.5 font-mono text-[11px] ${accent ? "bg-cyan-500/10 text-cyan-300" : "bg-white/5 text-white/70"}`}>
        {t}
      </span>
    ))}
  </div>
);

export const PluginDetailModal = ({ pluginId, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!pluginId) { setDetail(null); return; }
    setLoading(true);
    fetch(`${API}/api/plugins/${pluginId}`)
      .then((r) => { if (!r.ok) throw new Error("Not found"); return r.json(); })
      .then((data) => {
        setDetail(data);
        fetch(`${API}/api/track/view`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ plugin_id: data.id, plugin_name: data.name }),
        }).catch(() => {});
      })
      .catch(() => toast.error("Couldn't load plugin details"))
      .finally(() => setLoading(false));
  }, [pluginId]);

  const buy = (url, vendorName) => {
    if (detail) {
      fetch(`${API}/api/track/click`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          plugin_id: detail.id, plugin_name: detail.name,
          vendor_name: vendorName || "", vendor_url: url || "",
        }),
      }).catch(() => {});
    }
    window.open(url, "_blank", "noopener");
  };

  const searchFallback = detail
    ? `https://www.google.com/search?q=${encodeURIComponent(`${detail.name} ${detail.brand || ""} audio plugin buy`)}`
    : null;

  const vendors = detail?.vendors?.length
    ? detail.vendors
    : detail?.buy_url
    ? [{ name: detail.vendor_name || "Buy", url: detail.buy_url }]
    : [];

  return (
    <Dialog open={!!pluginId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="plugin-detail-modal" className="max-h-[85vh] max-w-3xl overflow-y-auto border-white/10 bg-[#111114] text-white">
        {!detail && (
          <div className="sr-only">
            <DialogTitle>Plugin details</DialogTitle>
            <DialogDescription>Loading plugin details</DialogDescription>
          </div>
        )}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-cyan-400" />
          </div>
        )}
        {detail && (
          <div>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-400">{detail.type}</div>
                <DialogTitle className="mt-1 font-heading text-2xl font-black tracking-tight">{detail.name}</DialogTitle>
                <DialogDescription className="mt-0.5 text-sm text-white/50">
                  {detail.brand} · <PriceTag price={detail.price} currency={detail.currency} />
                </DialogDescription>
              </div>
              <div className="flex shrink-0 flex-col gap-2 sm:items-end">
                {vendors.length === 0 && (
                  <button
                    data-testid="buy-plugin-btn"
                    onClick={() => buy(searchFallback, "Web search")}
                    className="flex items-center gap-2 rounded-lg bg-cyan-500 px-5 py-2.5 text-sm font-semibold text-black transition-colors duration-200 hover:bg-cyan-400"
                  >
                    Find it online <ExternalLink className="h-4 w-4" />
                  </button>
                )}
                {vendors.slice(0, 3).map((v, i) => (
                  <button
                    key={v.url}
                    data-testid={i === 0 ? "buy-plugin-btn" : `buy-vendor-btn-${i}`}
                    onClick={() => buy(v.url, v.name)}
                    className={`flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                      i === 0
                        ? "bg-cyan-500 text-black hover:bg-cyan-400"
                        : "border border-white/15 text-white/80 hover:border-cyan-500/40 hover:text-white"
                    }`}
                  >
                    {v.name || "Buy"} <ExternalLink className="h-4 w-4" />
                  </button>
                ))}
              </div>
            </div>

            {detail.description && <p className="mt-4 text-sm leading-relaxed text-white/60">{detail.description}</p>}

            <div className="mt-6 grid grid-cols-1 md:grid-cols-4" style={{ borderCollapse: "collapse" }}>
              {Object.keys(detail.suitability || {}).length > 0 && (
                <Cell label="Suitability" span={2} testId="detail-suitability">
                  <SuitabilityMeters suitability={detail.suitability} size="lg" />
                </Cell>
              )}
              {detail.characteristics?.length > 0 && (
                <Cell label="Sound character" span={2} testId="detail-characteristics">
                  <TagList items={detail.characteristics} accent />
                </Cell>
              )}
              {detail.genres?.length > 0 && (
                <Cell label="Genres" span={2} testId="detail-genres"><TagList items={detail.genres.slice(0, 8)} /></Cell>
              )}
              {detail.era_context?.length > 0 && (
                <Cell label="Era context" span={2} testId="detail-era"><TagList items={detail.era_context.slice(0, 6)} /></Cell>
              )}
              {detail.best_for?.length > 0 && (
                <Cell label="Best for" span={2} testId="detail-best-for">
                  <ul className="list-inside list-disc space-y-1">{detail.best_for.slice(0, 4).map((t) => <li key={t}>{t}</li>)}</ul>
                </Cell>
              )}
              {detail.techniques?.length > 0 && (
                <Cell label="Pro techniques" span={2} testId="detail-techniques">
                  <ul className="list-inside list-disc space-y-1">{detail.techniques.slice(0, 4).map((t) => <li key={t}>{t}</li>)}</ul>
                </Cell>
              )}
              {detail.famous_producers?.length > 0 && (
                <Cell label="Used by" span={2} testId="detail-producers"><TagList items={detail.famous_producers.slice(0, 6)} /></Cell>
              )}
              {detail.alternatives?.length > 0 && (
                <Cell label="Alternatives" span={2} testId="detail-alternatives">
                  <ul className="space-y-1">{detail.alternatives.slice(0, 4).map((t) => <li key={t}>{t}</li>)}</ul>
                </Cell>
              )}
              {(detail.formats?.length > 0 || detail.os_support?.length > 0) && (
                <Cell label="Formats" span={4} testId="detail-formats">
                  <TagList items={[...(detail.formats || []), ...(detail.os_support || [])].slice(0, 10)} />
                </Cell>
              )}
            </div>

            <FeedbackStrip detail={detail} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
